import { useState } from "react";
import { Bell, Send, Loader2, Mic2, FileText, KeyRound } from "lucide-react";
import { PinSettings } from "../components/PinSettings";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { EFFECTS } from "../lib/voiceEffects";
import { Switch } from "../components/ui/switch";
import { ExportPdfButton } from "../components/ExportPdfButton";

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);
const ZONES = Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : ["Europe/Paris"];

const Section = ({ icon: Icon, title, children, testid }) => (
  <section className="glass-card p-6 sm:p-8" data-testid={testid}>
    <h2 className="font-serif text-2xl text-[#F4F6FA] flex items-center gap-3 mb-6"><Icon size={20} className="text-[#E5C158]" /> {title}</h2>
    {children}
  </section>
);

const selectCls = "input-navy appearance-none cursor-pointer";

export default function Settings() {
  const { user, setUser, mergeUser } = useAuth();
  const { t, lang } = useI18n();
  const [sending, setSending] = useState(false);

  const save = async (patch) => {
    try {
      const { data } = await api.patch("/auth/preferences", { ...patch, reminder_lang: lang });
      mergeUser(data);
      toast.success(t("set_saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const test = async () => {
    setSending(true);
    try {
      await api.post("/reminders/test");
      toast.success(t("set_test_sent"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20 space-y-6 fade-up">
      <p className="label-gold">{t("set_kicker")}</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#F4F6FA] font-semibold mb-4">{t("set_title")}</h1>

      <Section icon={KeyRound} title={t("pin_title")} testid="settings-pin-section">
        <PinSettings />
      </Section>

      <Section icon={Bell} title={t("set_reminder")} testid="settings-reminder-section">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-[#F4F6FA]">{t("set_reminder_toggle")}</p>
            <p className="text-xs text-[#8E9BB4] mt-1">{t("set_reminder_desc")} <span className="text-[#C2C9DB]">{user.email}</span></p>
          </div>
          <Switch data-testid="settings-reminder-switch" checked={user.reminder_enabled} onCheckedChange={(v) => save({ reminder_enabled: v })} className="data-[state=checked]:bg-[#E5C158] data-[state=unchecked]:bg-white/15" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="label-gold block mb-2">{t("set_time")}</span>
            <select data-testid="settings-reminder-time" className={selectCls} value={user.reminder_time} onChange={(e) => save({ reminder_time: e.target.value })}>
              {TIMES.map((x) => <option key={x} value={x} className="bg-[#0C1322]">{x}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label-gold block mb-2">{t("set_tz")}</span>
            <select data-testid="settings-reminder-tz" className={selectCls} value={user.reminder_tz} onChange={(e) => save({ reminder_tz: e.target.value })}>
              {ZONES.map((z) => <option key={z} value={z} className="bg-[#0C1322]">{z.replace(/_/g, " ")}</option>)}
            </select>
          </label>
        </div>
        <p className="text-xs text-[#8E9BB4] mt-4">{t("set_reminder_note")}</p>
        <button data-testid="settings-reminder-test-btn" onClick={test} disabled={sending} className="mt-5 inline-flex items-center gap-2 text-sm text-[#E5C158] hover:text-[#F0D375] disabled:opacity-60">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t("set_test")}
        </button>
      </Section>

      <Section icon={Mic2} title={t("fx_label")} testid="settings-voice-section">
        <label className="block max-w-xs">
          <span className="label-gold block mb-2">{t("set_default_fx")}</span>
          <select data-testid="settings-default-effect" className={selectCls} value={user.default_voice_effect} onChange={(e) => save({ default_voice_effect: e.target.value })}>
            {EFFECTS.map((fx) => <option key={fx} value={fx} className="bg-[#0C1322]">{t(`fx_${fx}`)}</option>)}
          </select>
        </label>
      </Section>

      <Section icon={FileText} title={t("pdf_title")} testid="settings-export-section">
        <p className="text-sm text-[#8E9BB4] mb-5">{t("pdf_desc")}</p>
        <ExportPdfButton />
      </Section>
    </div>
  );
}
